import { useMutation, useQueryClient } from 'react-query';

import { CreateDocChronologyResponseDto } from '../../../../backend/src/matter/subset/chronology/doc-chronology/dto/create-doc-chron-response.dto';
import { TrackingEventsV2 } from '../../analytics/eventsV2';
import { trackCustomEvent } from '../../analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface CreateDocChronologyFromChronologyParameters {
  matterId: string;
  subsetId: string;
  chronologyId: string;
}

interface HookProps {
  onSuccess?: (data: CreateDocChronologyResponseDto) => void;
  onError?: (error: Error) => void;
}

export const useCreateDocChronologyFromChronology = ({ onSuccess, onError }: HookProps = {}) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ matterId, subsetId, chronologyId }: CreateDocChronologyFromChronologyParameters) => {
      const fetchConfig = getFetchConfig({ method: FetchMethod.POST });

      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/from-chronology/${chronologyId}`,
        fetchConfig,
      );

      if (!res.ok) {
        throw new Error('Failed to create document chronology');
      }

      return res.json() as Promise<CreateDocChronologyResponseDto>;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([QueryEntity.DocChronologies, { subsetId: variables.subsetId }]);
        trackCustomEvent(TrackingEventsV2.DocChronologyCreate);
        onSuccess?.(data);
      },
      onError: (error: Error) => {
        onError?.(error);
      },
    },
  );
};
